import WhatsAppIcon from "@/components/WhatsAppIcon";
import { usePageData } from "@/lib/pageData";
import { BUSINESS } from "@/lib/business";
import { useEffect, useState, createContext, useContext } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Star, ChevronDown } from "lucide-react";
import { api, waLink } from "@/lib/api";

const SettingsCtx = createContext(null);
export const useSettings = () => useContext(SettingsCtx);

const FALLBACK = {
  company_name: "Mynavodayans Online Services Private Limited",
  brand_name: "Mynavodayans",
  tagline: "Complete Business Solutions Across India",
  phone_display: "+91 94247 06212",
  phone_link: "tel:+919424706212",
  whatsapp_number: "919424706212",
  google_review_link: "https://g.page/r/CbpnKnHRzF7qEBI/review",
  about_short: "",
  logo_data_url: "",
  email: "",
  address: BUSINESS.address,
};

function normaliseSettings(value) {
  return { ...FALLBACK, ...value, address: value?.address || BUSINESS.address };
}

export function SettingsProvider({ children }) {
  const initial = usePageData();
  const [settings, setSettings] = useState(() => normaliseSettings(initial?.settings));
  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(normaliseSettings(r.data))).catch(() => {});
  }, []);
  return <SettingsCtx.Provider value={settings}>{children}</SettingsCtx.Provider>;
}

function Logo({ settings, light }) {
  const src = settings.logo_data_url || "/assets/mynavodayans-logo.png";
  const img = (
    <img src={src} alt="Mynavodayans Online Services logo" width={40} height={40}
         className={light ? "h-10 w-10 rounded-md bg-white object-contain p-0.5" : "h-10 w-10 object-contain"}
         data-testid="site-logo-image" />
  );
  return (
    <span className="flex items-center gap-2.5">
      {img}
      <span className="leading-tight">
        <span className={`block font-heading text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-[#1e2a78]"}`}>
          Mynavodayans
        </span>
        <span className={`block text-[10px] font-medium uppercase tracking-[0.14em] ${light ? "text-white/60" : "text-slate-500"}`}>
          Online Services Pvt. Ltd.
        </span>
      </span>
    </span>
  );
}

function Header({ settings, categories }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const navCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors ${isActive ? "text-[#b07f0a]" : "text-slate-700 hover:text-[#0a192f]"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl" data-testid="site-header">
      <div className="bg-[#0a192f]">
        <div className="container-x flex items-center justify-between py-1.5 text-xs text-white/80">
          <span>{settings.tagline}</span>
          <div className="flex items-center gap-4">
            <a href={settings.phone_link} className="flex items-center gap-1.5 font-semibold text-[#f5c542]" data-testid="header-call-link">
              <Phone size={13} /> {settings.phone_display}
            </a>
            <a href={waLink(settings.whatsapp_number, "Hello Mynavodayans, I would like to enquire about your services.")}
               target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1.5 hover:text-[#f5c542] sm:flex" data-testid="header-whatsapp-link">
              <WhatsAppIcon size={13} /> WhatsApp
            </a>
          </div>
        </div>
      </div>
      <div className="container-x flex h-16 items-center justify-between">
        <Link to="/" aria-label="Mynavodayans home" data-testid="header-logo-link"><Logo settings={settings} /></Link>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
          <NavLink to="/" className={navCls} data-testid="nav-home">Home</NavLink>
          <div className="group relative">
            <button className="flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-[#0a192f]" data-testid="nav-services-dropdown" aria-haspopup="true">
              Services <ChevronDown size={14} />
            </button>
            <div className="invisible absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <div className="grid grid-cols-2 gap-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                {categories.map((c) => (
                  <Link key={c.slug} to={`/services/category/${c.slug}`}
                        className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-amber-50 hover:text-[#0a192f]"
                        data-testid={`nav-category-${c.slug}`}>
                    {c.name} <span className="text-xs text-slate-400">({c.count})</span>
                  </Link>
                ))}
                <Link to="/services" className="col-span-2 mt-1 rounded-lg bg-[#0a192f] px-3 py-2 text-center text-sm font-semibold text-white" data-testid="nav-all-services">
                  Browse All Services
                </Link>
              </div>
            </div>
          </div>
          <NavLink to="/service-areas" className={navCls} data-testid="nav-service-areas">Service Areas — India</NavLink>
          <NavLink to="/about" className={navCls} data-testid="nav-about">About Us</NavLink>
          <NavLink to="/contact" className={navCls} data-testid="nav-contact">Contact</NavLink>
          <button onClick={() => navigate("/contact")} className="btn-gold !px-5 !py-2.5" data-testid="header-quote-btn">Request a Quote</button>
        </nav>
        <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} data-testid="mobile-menu-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <nav className="border-t border-slate-200 bg-white lg:hidden" aria-label="Mobile navigation" data-testid="mobile-menu">
          <div className="container-x flex flex-col py-4">
            {[["/", "Home"], ["/services", "All Services"], ["/service-areas", "Service Areas — India"], ["/about", "About Us"], ["/contact", "Contact"]].map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="border-b border-slate-100 py-3 text-sm font-semibold text-slate-800" data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                {label}
              </Link>
            ))}
            <p className="pt-3 text-xs font-bold uppercase tracking-wider text-slate-400">Service Categories</p>
            <div className="grid grid-cols-1 gap-0.5 py-2">
              {categories.map((c) => (
                <Link key={c.slug} to={`/services/category/${c.slug}`} onClick={() => setOpen(false)}
                      className="rounded-md px-2 py-2 text-sm text-slate-600 hover:bg-amber-50" data-testid={`mobile-nav-cat-${c.slug}`}>
                  {c.name}
                </Link>
              ))}
            </div>
            <Link to="/contact" onClick={() => setOpen(false)} className="btn-gold mt-3" data-testid="mobile-quote-btn">Request a Quote</Link>
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer({ settings, categories }) {
  return (
    <footer className="bg-[#0a192f] text-white" data-testid="site-footer">
      <div className="container-x grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo settings={settings} light />
          <p className="mt-4 text-sm leading-relaxed text-white/70">{settings.about_short || "Business registration, licensing assistance, compliance services, and website and mobile app development — across India, coordinated from our office in Satna, Madhya Pradesh."}</p>
          <a href={settings.google_review_link} target="_blank" rel="noopener noreferrer"
             className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#d4a017]/50 px-4 py-2 text-sm font-semibold text-[#f5c542] transition-colors hover:bg-[#d4a017]/10"
             data-testid="footer-google-review-link">
            <Star size={15} /> Review Us on Google
          </a>
        </div>
        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#f5c542]">Quick Links</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-white/70">
            <li><Link to="/" className="hover:text-white">Home</Link></li>
            <li><Link to="/services" className="hover:text-white">Services Directory</Link></li>
            <li><Link to="/service-areas" className="hover:text-white">Service Areas — India</Link></li>
            <li><Link to="/madhya-pradesh" className="text-white/55 hover:text-white">Madhya Pradesh regional details</Link></li>
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-white">Request a Quote</Link></li>
            <li><a href="/sitemap.xml" className="hover:text-white">XML Sitemap</a></li>
          </ul>
        </div>
        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#f5c542]">Popular Services</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-white/70">
            <li><Link to="/services/gst-registration" className="hover:text-white">GST Registration</Link></li>
            <li><Link to="/services/udyam-msme-registration" className="hover:text-white">Udyam / MSME Registration</Link></li>
            <li><Link to="/services/company-registration" className="hover:text-white">Company Registration</Link></li>
            <li><Link to="/services/shop-establishment-registration" className="hover:text-white">Shop & Establishment</Link></li>
            <li><Link to="/services/fssai-licensing" className="hover:text-white">FSSAI Licensing</Link></li>
            <li><Link to="/services/custom-website-development" className="hover:text-white">Website Development</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#f5c542]">Contact</h3>
          <ul className="mt-4 space-y-3 text-sm text-white/70">
            <li>
              <a href={settings.phone_link} className="flex items-center gap-2 font-semibold text-white hover:text-[#f5c542]" data-testid="footer-call-link">
                <Phone size={15} className="text-[#f5c542]" /> {settings.phone_display}
              </a>
            </li>
            <li>
              <a href={waLink(settings.whatsapp_number, "Hello Mynavodayans, I would like to enquire about your services.")}
                 target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-whatsapp-link">
                <WhatsAppIcon size={15} className="text-[#f5c542]" /> Chat on WhatsApp
              </a>
            </li>
            <li className="text-white/60">Based in Satna · Serving businesses across India</li>
            {settings.email && <li>{settings.email}</li>}
            {settings.address && <li><address className="not-italic leading-relaxed" data-testid="office-address">{settings.address}</address></li>}
            <li><a href={BUSINESS.maps} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#f5c542] hover:underline" data-testid="footer-maps-link">Find our Satna office on Google Maps</a></li>
          </ul>
          <div className="mt-5 flex gap-4 text-xs text-white/50">
            <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms of Use</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x flex flex-col items-center justify-between gap-2 py-5 text-xs text-white/50 sm:flex-row">
          <span>© {new Date().getFullYear()} {settings.company_name}. All rights reserved.</span>
          <span>A private service provider. Licences and approvals are issued by the relevant government authorities.</span>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  const settings = useSettings();
  const initial = usePageData();
  const [categories, setCategories] = useState(initial?.categories || []);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);
  return (
    <div className="flex min-h-screen flex-col pb-[calc(3.5rem_+_env(safe-area-inset-bottom,0px))] md:pb-0">
      <Header settings={settings} categories={categories} />
      <main className="flex-1">{children}</main>
      <Footer settings={settings} categories={categories} />
      <a href={waLink(settings.whatsapp_number, "Hello Mynavodayans, I would like to enquire about your services.")}
         target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp"
         className="fixed bottom-6 right-6 z-40 hidden h-14 w-14 items-center justify-center rounded-full bg-[#25d366] text-white shadow-[0_8px_24px_-4px_rgba(37,211,102,0.6)] transition-transform hover:scale-105 md:flex"
         data-testid="floating-whatsapp-btn">
        <WhatsAppIcon size={26} />
      </a>
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 border-t border-slate-200 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:hidden" data-testid="mobile-bottom-bar" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <a href={settings.phone_link} className="flex items-center justify-center gap-2 bg-[#0a192f] py-3.5 text-sm font-bold text-white" data-testid="mobile-call-btn">
          <Phone size={17} /> Call Now
        </a>
        <a href={waLink(settings.whatsapp_number, "Hello Mynavodayans, I would like to enquire about your services.")}
           target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#25d366] py-3.5 text-sm font-bold text-white" data-testid="mobile-whatsapp-btn">
          <WhatsAppIcon size={17} /> WhatsApp
        </a>
      </div>
    </div>
  );
}
